import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Select,
  TextField,
  Autocomplete,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";

import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { exportPurchasePaymentsToPDF } from "../../utils/exportPurchasePayments";

const firstCurrencyValue = (obj) => {
  if (obj && typeof obj === "object") {
    const firstEntry = Object.values(obj)[0];
    if (firstEntry && typeof firstEntry === "object")
      return firstEntry.source ?? "-";
    return firstEntry ?? "-";
  }
  return obj ?? "-";
};

export default function PurchasePaymentTable() {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [selectedPurchase, setSelectedPurchase] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const open = Boolean(anchorEl);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (startDate) params.set("start_date", startDate.format("YYYY-MM-DD"));
    if (endDate) params.set("end_date", endDate.format("YYYY-MM-DD"));
    if (selectedSupplier) params.set("supplier", selectedSupplier);
    if (selectedPurchase) params.set("purchase", selectedPurchase);
    if (searchTerm.trim()) params.set("search", searchTerm.trim());
    if (rowsPerPage !== -1) {
      params.set("page", String(page));
      params.set("page_size", String(rowsPerPage));
    } else {
      params.set("page_size", "all");
    }
    return params.toString();
  }, [startDate, endDate, selectedSupplier, selectedPurchase, searchTerm, page, rowsPerPage]);

  const {
    data: paymentsData,
    isLoading,
    isError,
  } = useFetchObjects(
    ["purchase-payments-report", queryString],
    `shop/purchase-payments/?${queryString}`,
    token
  );

  // Fetch suppliers for filter dropdown
  const { data: suppliersData } = useFetchObjects(
    ["suppliers"],
    "shop/suppliers",
    token
  );
  const { data: purchasesData } = useFetchObjects(
    ["purchase-payment-report-purchases"],
    "shop/purchases/?page_size=all",
    token
  );

  const suppliers = useMemo(() => suppliersData?.results || [], [suppliersData]);
  const purchases = useMemo(
    () => purchasesData?.results || purchasesData || [],
    [purchasesData]
  );

  const payments = useMemo(() => {
    const data = paymentsData?.results || paymentsData;
    if (!Array.isArray(data)) return [];

    return data.map((payment) => ({
      ...payment,
      supplier_name: payment.purchase_info?.supplier,
      purchase_reference: `Purchase #${payment.purchase_info?.id || payment.purchase || "N/A"}`,
      currency: payment.currency_details?.title || "AF",
      total_remained_amount: payment.purchase_info?.total_remained_amount,
    }));
  }, [paymentsData]);

  const totalCount = paymentsData?.count || payments.length;

  // Calculate summary totals
  const summaryTotals = useMemo(() => {
    return payments.reduce((acc, payment) => {
      const amount = parseFloat(payment.amount) || 0;
      const totalRemained = parseFloat(firstCurrencyValue(payment.total_remained_amount)) || 0;
      
      return {
        totalAmount: acc.totalAmount + amount,
        totalRemained: acc.totalRemained + totalRemained
      };
    }, { totalAmount: 0, totalRemained: 0 });
  }, [payments]);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleExportClick = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    const base = `purchase_payments_${today}`;
    if (type === "pdf") exportPurchasePaymentsToPDF(payments, `${base}.pdf`);
    handleClose();
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <LinearProgress sx={{ width: "90%" }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="error">
          {t("ReportIndex.error")}{" "}
          <RefreshIcon sx={{ cursor: "pointer", verticalAlign: "middle" }} />
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Filters */}
      <Box sx={{ flexGrow: 1, mb: 5, m: "15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <DatePicker
                  label={t("ReportIndex.datepicker_start")}
                  value={startDate}
                  onChange={(value) => { setStartDate(value); setPage(1); }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label={t("ReportIndex.datepicker_end")}
                  value={endDate}
                  onChange={(value) => { setEndDate(value); setPage(1); }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <Autocomplete
                  fullWidth
                  options={suppliers}
                  value={suppliers.find((supplier) => String(supplier.id) === String(selectedSupplier)) || null}
                  getOptionLabel={(supplier) => supplier?.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_, supplier) => {
                      setSelectedSupplier(supplier ? String(supplier.id) : "");
                      setSelectedPurchase("");
                      setPage(1);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("ReportIndex.select_supplier", "Supplier")}
                      placeholder={t("ReportIndex.all_suppliers", "All suppliers")}
                    />
                  )}
                />
                <TextField
                  label={t("purchasePayment.searchPayments", "Search payments")}
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                  placeholder="Supplier, purchase number, note, or amount"
                  fullWidth
                />
                <FormControl fullWidth>
                  <Select
                    value={selectedPurchase}
                    onChange={(e) => {
                      setSelectedPurchase(e.target.value);
                      setPage(1);
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">{t("ReportIndex.all_purchases")}</MenuItem>
                    {purchases.map((purchase) => (
                      <MenuItem key={purchase.id} value={purchase.id}>
                        {`Purchase #${purchase.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedSupplier("");
                    setSelectedPurchase("");
                    setSearchTerm("");
                    setPage(1);
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                  }}
                >
                  {t("ReportIndex.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-button"
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    fullWidth
                    sx={{
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                      flexShrink: 0,
                      height: 55,
                    }}
                  >
                    {t("ReportIndex.btn_export")}
                  </Button>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{ "aria-labelledby": "export-button" }}
                  >
                    <MenuItem onClick={() => handleExportClick("excel")}>
                      {t("ReportIndex.export_excel")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("pdf")}>
                      {t("ReportIndex.export_pdf")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("none")}>
                      {t("ReportIndex.export_none")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, ml: 2 }}>
        <Typography>{t("reports.rowsPerPage")}</Typography>
        <FormControl size="small">
          <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
            <MenuItem value={-1}>{t("reports.all")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Divider />

      {/* Purchase Payment Table */}
      <TableContainer component={Paper}>
        <Table aria-label="purchase payment table">
          <TableHead>
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography variant="h4">
                  {t("reports.purchasePayment_tableTitle")}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("reports.purchasePayment_table_id")}</TableCell>
              <TableCell>{t("reports.purchasePayment_table_supplier")}</TableCell>
              <TableCell>{t("reports.purchasePayment_table_reference")}</TableCell>
              <TableCell>{t("reports.purchasePayment_table_amount")}</TableCell>
              <TableCell>{t("reports.purchasePayment_table_currency")}</TableCell>
              <TableCell>{t("reports.purchasePayment_table_paymentDate")}</TableCell>
              <TableCell>{t("reports.purchasePayment_table_totalRemained")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>{payment.id}</TableCell>
                <TableCell>{payment.supplier_name || "-"}</TableCell>
                <TableCell>{payment.purchase_reference || "-"}</TableCell>
                <TableCell>{payment.amount || "0.00"}</TableCell>
                <TableCell>{payment.currency || "AF"}</TableCell>
                <TableCell>
                  {payment.payment_date
                    ? new Date(payment.payment_date).toLocaleString()
                    : payment.created_at
                    ? new Date(payment.created_at).toLocaleString()
                    : "-"}
                </TableCell>
                <TableCell>
                  {firstCurrencyValue(payment.total_remained_amount) || "0"} {payment.currency || "AF"}
                </TableCell>
              </TableRow>
            ))}
            {/* Summary Row */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>{t('Summary')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalAmount.toFixed(2)}
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalRemained.toFixed(2)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {rowsPerPage !== -1 && (
        <Grid container justifyContent="center" alignItems="center" padding="10px">
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page}
            onChange={(event, newPage) => setPage(newPage)}
            variant="outlined"
            color="primary"
          />
        </Grid>
      )}
    </>
  );
}
